import { useEffect, useRef, useState } from 'react';

import { useTenantSwitch } from '~/hooks/useTenantSwitch';
import { useAuthStore } from '~/store/auth.store';
import type { AvailableTenant } from '~/store/auth.store';

import { resolveWrongSubsidiary } from '../utils/resolve-wrong-subsidiary';

/** Stable empty reference — a fresh `[]` from a Zustand selector loops `useSyncExternalStore`. */
const EMPTY_TENANTS: AvailableTenant[] = [];

/** sessionStorage key — one auto-switch attempt per experiment per tab. */
const attemptKey = (experimentId: string) => `xsub-redirect:${experimentId}`;

// sessionStorage can throw (Safari private mode, storage disabled) — never let
// the loop-guard bookkeeping take down the page. Losing the marker only leaves
// the `owner !== active` check as the sole loop bound, which is still safe.
function isAttempted(experimentId: string): boolean {
  try {
    return sessionStorage.getItem(attemptKey(experimentId)) === '1';
  } catch {
    return false;
  }
}

function markAttempted(experimentId: string): void {
  try {
    sessionStorage.setItem(attemptKey(experimentId), '1');
  } catch {
    /* storage unavailable — see isAttempted */
  }
}

/** Clear the loop guard once the experiment loads, so a later re-open can auto-switch again. */
export function clearWrongSubsidiaryAttempt(experimentId: string): void {
  try {
    sessionStorage.removeItem(attemptKey(experimentId));
  } catch {
    /* storage unavailable — see isAttempted */
  }
}

export type WrongSubsidiaryState =
  | { status: 'idle' }
  | { status: 'redirecting'; owner: AvailableTenant };

/**
 * Detects a recoverable "wrong subsidiary" experiment-load error and auto-switches
 * to the owning subsidiary, landing back on `redirectTo` after the reload. Capped
 * at one attempt per experiment per tab so a backend that names the wrong owner
 * can't trap the user in a switch loop; no-ops for any other error so the caller
 * can render its own fallback. If the switch call itself fails (no navigation), it
 * stops reporting `redirecting` so the caller can fall back to manual recovery.
 */
export function useWrongSubsidiaryRedirect(
  experimentId: string,
  error: unknown,
  redirectTo: string,
): WrongSubsidiaryState {
  const accessibleTenants = useAuthStore((s) => s.user?.accessibleTenants ?? EMPTY_TENANTS);
  const activeTenantId = useAuthStore((s) => s.user?.activeTenantId);
  const { switchTenant } = useTenantSwitch();

  const owner = resolveWrongSubsidiary(error, accessibleTenants, activeTenantId);
  // Kept independent of `owner` so the null-checks below stay meaningful to the
  // type-checker (and the no-unnecessary-condition lint) rather than collapsing.
  const alreadyTried = isAttempted(experimentId);

  // Belt-and-braces against re-firing across re-renders within this mount;
  // the sessionStorage marker covers re-mounts and the post-reload tab.
  const firedRef = useRef(false);

  // The backend switch failed (no navigation happened) — drop the interstitial
  // so the caller falls through to its manual fallback instead of spinning.
  const [switchFailed, setSwitchFailed] = useState(false);

  useEffect(() => {
    if (owner === null || alreadyTried || firedRef.current) return;
    firedRef.current = true;
    markAttempted(experimentId);
    void switchTenant(owner.id, { redirectTo }).then((navigating) => {
      if (!navigating) setSwitchFailed(true);
    });
  }, [owner, alreadyTried, experimentId, redirectTo, switchTenant]);

  return owner !== null && !alreadyTried && !switchFailed
    ? { status: 'redirecting', owner }
    : { status: 'idle' };
}
